import { useState } from "react";
import { BellOff, BellRing, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");

// Active ⇄ Dormant account toggle with a single confirmation step.
// Dormant = every outgoing Clinq email to this client is suppressed.
export const AccountStatusToggle = ({ r, onChanged }) => {
  const [confirm, setConfirm] = useState(null); // "active" | "dormant" | null
  const [busy, setBusy] = useState(false);
  const id = r.company_id;
  const dormant = r.account_status === "dormant";

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/rm/clients/${id}/account-status`, { status: confirm });
      onChanged(id, {
        account_status: data.account_status,
        account_status_changed_at: data.account_status_changed_at,
        account_status_changed_by: data.account_status_changed_by,
      });
      toast.success(confirm === "dormant" ? "Client set to Dormant — emails paused" : "Client reactivated — emails resume");
      setConfirm(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update — try again"); }
    setBusy(false);
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`rm-account-status-${id}`}>
      <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-wide ${
        dormant ? "border-slate-300 bg-slate-100 text-slate-600" : "border-emerald-300 bg-emerald-50 text-emerald-700"}`}
        data-testid={`rm-account-badge-${id}`}>
        {dormant ? <BellOff size={11} /> : <BellRing size={11} />} {dormant ? "Dormant" : "Active"}
      </span>
      <button type="button" onClick={() => setConfirm(dormant ? "active" : "dormant")}
        className="rounded-md border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:border-[#0A8B85] hover:text-[#0A8B85]"
        data-testid={`rm-account-toggle-${id}`}>
        {dormant ? "Set Active" : "Set Dormant"}
      </button>
      {r.account_status_changed_at && (
        <span className="text-[10px] text-slate-400" data-testid={`rm-account-log-${id}`}>
          {dormant ? "Paused" : "Reactivated"} {fmt(r.account_status_changed_at)}{r.account_status_changed_by ? ` by ${r.account_status_changed_by}` : ""}
        </span>
      )}

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setConfirm(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}
            data-testid="rm-account-confirm-dialog">
            <div className="flex items-start justify-between">
              <h3 className="text-base font-bold text-slate-900">
                {confirm === "dormant" ? "Set client to Dormant?" : "Reactivate this client?"}
              </h3>
              <button onClick={() => setConfirm(null)} className="text-slate-400 hover:text-slate-600" data-testid="rm-account-confirm-close"><X size={18} /></button>
            </div>
            <p className="mt-1 text-xs text-slate-500">{r.client_name} · {r.company_name}</p>
            <p className="mt-4 text-sm text-slate-700">
              {confirm === "dormant"
                ? "All outgoing Clinq emails to this client will stop — lifecycle nudges, report reminders and campaigns. The change is logged under your name."
                : "Email communications resume from where they left off in the lifecycle sequence. The change is logged under your name."}
            </p>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setConfirm(null)} className="flex-1 rounded-lg border border-slate-200 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                data-testid="rm-account-confirm-cancel">Cancel</button>
              <button onClick={apply} disabled={busy}
                className={`flex-1 rounded-lg py-2.5 text-sm font-bold text-white disabled:opacity-50 ${confirm === "dormant" ? "bg-slate-700 hover:bg-slate-800" : "bg-[#0A8B85] hover:bg-[#097a74]"}`}
                data-testid="rm-account-confirm-apply">
                {busy ? "Saving…" : confirm === "dormant" ? "Yes, set Dormant" : "Yes, set Active"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
